import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { getTicketAllocationSettings } from '../lib/ticketAllocationUtils';

export interface Event {
  id: string;
  track_image_id: string;
  name: string;
  description: string;
  event_date: string;
  registration_deadline: string;
  is_active: boolean;
  ticket_shop_url?: string;
  track_image: {
    title: string;
    description: string;
    storage_path: string;
    url?: string;
  };
  event_categories: EventCategory[];
}

export interface EventCategory {
  id: string;
  name: string;
  vehicle_type: 'car' | 'bike';
  time_range: string;
  tree_format: string;
  registration_fee: number;
  first_place_prize: number;
  second_place_prize: number;
  wristbands_included?: number;
  tickets_included?: number;
  use_percentage_prizes?: boolean;
  first_place_percentage?: number;
  second_place_percentage?: number;
}

export interface DriverProfile {
  id: string;
  vehicle_type: 'auto' | 'motorcycle';
  is_complete: boolean;
  vehicle_info: any;
  personal_info: any;
}

export interface EventRegistration {
  id: string;
  event_id: string;
  category_id: string;
  amount_paid: number;
  status: string;
  payment_status: string;
  created_at: string;
  event: {
    name: string;
    event_date: string;
  };
  category: {
    name: string;
    vehicle_type: string;
    registration_fee: number;
  };
}

export const useEvents = (userId: string | undefined) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [driverProfiles, setDriverProfiles] = useState<DriverProfile[]>([]);
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    fetchEvents();
    if (userId) {
      fetchUserRole();
      fetchDriverProfiles();
      fetchUserRegistrations();
    }
  }, [userId]);

  // Helper function to format date for display (avoiding timezone issues)
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    // Parse as local date to avoid timezone conversion
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to check if an event is upcoming
  const isEventUpcoming = (eventDate: string): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const [year, month, day] = eventDate.split('-');
    const event = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return event >= today;
  };

  // Function to generate alternative date formats for an event date
  const generateDateFormats = (dateString: string): string[] => {
    if (!dateString) return [];
    
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    const monthNames = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    
    const monthShortNames = [
      'jan', 'feb', 'mar', 'apr', 'may', 'jun',
      'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
    ];
    
    const monthIndex = parseInt(month) - 1;
    const monthName = monthNames[monthIndex];
    const monthShortName = monthShortNames[monthIndex];
    
    return [
      // Standard format from formatDateForDisplay (e.g., "Jun 15, 2025")
      formatDateForDisplay(dateString).toLowerCase(),
      
      // Numeric formats
      `${day}/${month}/${year}`,
      `${day}-${month}-${year}`,
      `${day}/${month.padStart(2, '0')}/${year}`,
      `${day}-${month.padStart(2, '0')}-${year}`,
      
      // Month name formats
      `${day} ${monthName} ${year}`,
      `${day} ${monthShortName} ${year}`,
      `${monthName} ${day} ${year}`,
      `${monthShortName} ${day} ${year}`,
      `${day} ${monthName}`,
      `${day} ${monthShortName}`,
      
      // Month name formats with day first
      `${parseInt(day)} ${monthName} ${year}`,
      `${parseInt(day)} ${monthShortName} ${year}`,
      `${monthName} ${parseInt(day)} ${year}`,
      `${monthShortName} ${parseInt(day)} ${year}`,
      `${parseInt(day)} ${monthName}`,
      `${parseInt(day)} ${monthShortName}`,
      
      // Just month and year
      `${monthName} ${year}`,
      `${monthShortName} ${year}`,
      
      // Just day and month
      `${day} ${month}`,
      `${month} ${day}`,
      
      // Just numbers
      day,
      month,
      year
    ];
  };

  const fetchUserRole = async () => {
    if (!userId) return;
    
    try {
      const { data, error } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setUserRole(data?.role || '');
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          track_image:track_images(
            title,
            description,
            storage_path
          ),
          event_categories(
            id,
            name,
            vehicle_type,
            time_range,
            tree_format,
            registration_fee,
            first_place_prize,
            second_place_prize,
            use_percentage_prizes,
            first_place_percentage,
            second_place_percentage
          )
        `)
        .eq('is_active', true)
        .order('event_date', { ascending: true });

      if (error) throw error;

      // Get public URLs for track images and calculate ticket allocation for categories
      const eventsWithUrls = await Promise.all(
        (data || []).map(async (event) => {
          let eventWithUrl = event;
          
          // Get public URL for track image
          if (event.track_image?.storage_path) {
            const { data: { publicUrl } } = supabase
              .storage
              .from('track-images')
              .getPublicUrl(event.track_image.storage_path);
            
            eventWithUrl = {
              ...event,
              track_image: {
                ...event.track_image,
                url: publicUrl
              }
            };
          }

          // Get ticket allocation settings for this event
          const ticketSettings = await getTicketAllocationSettings(event.id);

          // Calculate ticket allocation for each category
          if (event.event_categories && event.event_categories.length > 0) {
            const categoriesWithTickets = event.event_categories.map(category => {
              // Only calculate if tickets are enabled
              if (ticketSettings.tickets_enabled) {
                const { categories } = calculateTicketAllocationLogic([category], [], false, 1, ticketSettings);
                const categoryWithTickets = categories[0];
                
                return {
                  ...category,
                  wristbands_included: categoryWithTickets?.wristbands_included || 0,
                  tickets_included: categoryWithTickets?.tickets_included || 0
                };
              } else {
                return {
                  ...category,
                  wristbands_included: 0,
                  tickets_included: 0
                };
              }
            });

            eventWithUrl = {
              ...eventWithUrl,
              event_categories: categoriesWithTickets
            };
          }

          return eventWithUrl;
        })
      );

      setEvents(eventsWithUrls);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverProfiles = async () => {
    if (!userId) return;
    
    try {
      const { data, error } = await supabase
        .from('driver_profiles')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      setDriverProfiles(data || []);
    } catch (error) {
      console.error('Error fetching driver profiles:', error);
    }
  };

  const fetchUserRegistrations = async () => {
    if (!userId) return;
    
    try {
      const { data, error } = await supabase
        .from('event_registrations')
        .select(`
          *,
          event:events!event_registrations_event_id_fkey(name, event_date, ticket_shop_url),
          category:event_categories(name, vehicle_type, registration_fee)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRegistrations(data || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    }
  };

  // Calculate total amount to pay for the current event only
  const getEventTotalAmountToPay = (eventId: string) => {
    return registrations
      .filter(reg => reg.event_id === eventId && reg.payment_status === 'pending')
      .reduce((total, reg) => total + reg.amount_paid, 0);
  };

  // Get user's registrations for a specific event
  const getUserEventRegistrations = (eventId: string) => {
    return registrations.filter(reg => reg.event_id === eventId);
  };

  // Check if user is registered for any category in an event
  const isUserRegisteredForEvent = (eventId: string) => {
    return registrations.some(reg => reg.event_id === eventId);
  };

  // Get registration count for an event
  const getEventRegistrationCount = (eventId: string) => {
    return registrations.filter(reg => reg.event_id === eventId).length;
  };

  // Function to refresh registrations data (for real-time updates)
  const refreshRegistrations = async () => {
    await fetchUserRegistrations();
  };

  return {
    events,
    driverProfiles,
    registrations,
    loading,
    userRole,
    formatDateForDisplay,
    isEventUpcoming,
    generateDateFormats,
    getEventTotalAmountToPay,
    getUserEventRegistrations,
    isUserRegisteredForEvent,
    getEventRegistrationCount,
    fetchUserRegistrations,
    refreshRegistrations
  };
};

// Import the calculateTicketAllocationLogic function from ticketAllocationUtils
import { calculateTicketAllocationLogic } from '../lib/ticketAllocationUtils';